/**
 * SkillSelectionModal Component
 * Modal for selecting skills before creating Express/Standard interviews.
 * Phase 1: Select skills (badges)
 * Phase 2: Set years of experience per selected skill (stepper inputs)
 */

import { useState, useEffect } from 'react'
import {
  XMarkIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline'
import { toast } from 'react-hot-toast'
import { aiInterviewsApi } from '../../services/api'
import type { JobSkillsResponse, SkillWithExperience } from '../../types/interview'

interface SkillSelectionModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (selectedSkills: SkillWithExperience[]) => void
  jobId: string
  interviewType: 'express' | 'standard'
  jobTitle: string
}

export default function SkillSelectionModal({
  isOpen,
  onClose,
  onConfirm,
  jobId,
  interviewType,
  jobTitle,
}: SkillSelectionModalProps) {
  const [selectedSkillNames, setSelectedSkillNames] = useState<string[]>([])
  const [skillYears, setSkillYears] = useState<Record<string, number>>({})
  const [skillQuestionCounts, setSkillQuestionCounts] = useState<Record<string, number>>({}) // NEW: question count per skill
  const [jobSkills, setJobSkills] = useState<JobSkillsResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [skillCount, setSkillCount] = useState<number>(3) // Dynamic skill count for standard interviews

  const requiredCount = interviewType === 'express' ? 1 : skillCount

  useEffect(() => {
    if (isOpen) {
      fetchJobSkills()
      setSelectedSkillNames([])
      setSkillYears({})
      setSkillQuestionCounts({}) // Reset question counts
      setSkillCount(3) // Reset to default 3 skills for standard interviews
    }
  }, [isOpen, jobId])

  const fetchJobSkills = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await aiInterviewsApi.getJobSkills(jobId)
      setJobSkills(response.data)
    } catch (err: any) {
      console.error('Failed to fetch job skills:', err)
      setError(err?.response?.data?.detail || 'Failed to load job skills')
      toast.error('Failed to load job skills')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSkillToggle = (skill: string) => {
    setSelectedSkillNames(prev => {
      if (prev.includes(skill)) {
        return prev.filter(s => s !== skill)
      } else {
        if (prev.length < requiredCount) {
          return [...prev, skill]
        }
        // Replace the last selected skill
        return [...prev.slice(0, -1), skill]
      }
    })
  }

  const handleYearChange = (skill: string, value: number) => {
    setSkillYears(prev => ({
      ...prev,
      [skill]: Math.max(0, Math.min(50, value)),
    }))
  }

  const handleQuestionCountChange = (skill: string, value: number) => {
    const newValue = Math.max(1, Math.min(10, value))
    console.log(`Question count changed for ${skill}: ${newValue}`)
    setSkillQuestionCounts(prev => ({
      ...prev,
      [skill]: newValue,
    }))
  }

  const handleConfirm = () => {
    if (selectedSkillNames.length !== requiredCount) {
      toast.error(`Please select exactly ${requiredCount} skill${requiredCount > 1 ? 's' : ''}`)
      return
    }
    const skillsWithExperience: SkillWithExperience[] = selectedSkillNames.map(name => ({
      name,
      years_of_experience: skillYears[name] ?? 5,
      question_count: skillQuestionCounts[name] ?? 10, // Default to 10 if not set
    }))
    console.log('=== SKILL SELECTION DEBUG ===')
    console.log('skillQuestionCounts state:', skillQuestionCounts)
    console.log('Skills being sent:', skillsWithExperience)
    console.log('============================')
    onConfirm(skillsWithExperience)
  }

  if (!isOpen) return null

  const showYearPanel = selectedSkillNames.length === requiredCount

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-0 border w-[95%] max-w-xl shadow-lg rounded-lg bg-white max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center z-10">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Select Skills for {interviewType === 'express' ? 'Express' : 'Standard'} Interview
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {jobTitle}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Loading skills...</span>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <ExclamationCircleIcon className="mx-auto h-12 w-12 text-red-400" />
              <h3 className="mt-4 text-lg font-medium text-gray-900">Error Loading Skills</h3>
              <p className="mt-2 text-sm text-gray-500">{error}</p>
              <button
                onClick={fetchJobSkills}
                className="mt-4 px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-800"
              >
                Try Again
              </button>
            </div>
          ) : (
            <>
              {/* Skill Count Selector (for Standard interviews only) */}
              {interviewType === 'standard' && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-4">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <label className="text-sm font-medium text-purple-900 block">
                        Number of skills to assess
                      </label>
                      <p className="text-xs text-purple-700 mt-0.5">
                        Choose between 2-5 skills for your interview
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      <select
                        value={skillCount}
                        onChange={(e) => {
                          const newCount = parseInt(e.target.value)
                          setSkillCount(newCount)
                          // If current selection exceeds new count, trim it
                          if (selectedSkillNames.length > newCount) {
                            setSelectedSkillNames(prev => prev.slice(0, newCount))
                          }
                        }}
                        className="px-3 py-2 border border-purple-300 rounded-lg text-sm font-medium text-purple-900 bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 min-w-[110px]"
                      >
                        <option value={2}>2 skills</option>
                        <option value={3}>3 skills</option>
                        <option value={4}>4 skills</option>
                        <option value={5}>5 skills</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {/* Instructions */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 overflow-hidden">
                <p className="text-sm text-blue-800 break-words">
                  Select <strong>{requiredCount}</strong> skill{requiredCount > 1 ? 's' : ''} for the {interviewType === 'express' ? 'Express' : 'Standard'} interview,
                  then set your years of experience for each.
                </p>
              </div>

              {/* Selection Counter */}
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-medium text-gray-700">
                  Selected: {selectedSkillNames.length} / {requiredCount}
                </span>
                {showYearPanel && (
                  <span className="flex items-center text-sm text-green-600">
                    <CheckCircleIcon className="w-4 h-4 mr-1" />
                    Set experience below
                  </span>
                )}
              </div>

              {/* Required Skills */}
              {jobSkills?.required_skills && jobSkills.required_skills.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                    <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                    Required Skills
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {jobSkills.required_skills.map((skill) => (
                      <button
                        key={skill}
                        onClick={() => handleSkillToggle(skill)}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                          selectedSkillNames.includes(skill)
                            ? 'bg-blue-600 text-white ring-2 ring-blue-600 ring-offset-2'
                            : 'bg-red-100 text-red-800 hover:bg-red-200'
                        }`}
                      >
                        {skill}
                        {selectedSkillNames.includes(skill) && (
                          <span className="ml-1.5">
                            ({selectedSkillNames.indexOf(skill) + 1})
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Preferred Skills */}
              {jobSkills?.preferred_skills && jobSkills.preferred_skills.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                    <span className="w-2 h-2 bg-gray-400 rounded-full mr-2"></span>
                    Preferred Skills
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {jobSkills.preferred_skills
                      .filter(skill => !jobSkills.required_skills.includes(skill))
                      .map((skill) => (
                        <button
                          key={skill}
                          onClick={() => handleSkillToggle(skill)}
                          className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                            selectedSkillNames.includes(skill)
                              ? 'bg-blue-600 text-white ring-2 ring-blue-600 ring-offset-2'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {skill}
                          {selectedSkillNames.includes(skill) && (
                            <span className="ml-1.5">
                              ({selectedSkillNames.indexOf(skill) + 1})
                            </span>
                          )}
                        </button>
                      ))}
                  </div>
                </div>
              )}

              {/* No Skills Warning */}
              {(!jobSkills?.all_skills || jobSkills.all_skills.length === 0) && (
                <div className="text-center py-8">
                  <ExclamationCircleIcon className="mx-auto h-12 w-12 text-yellow-400" />
                  <h3 className="mt-4 text-lg font-medium text-gray-900">No Skills Found</h3>
                  <p className="mt-2 text-sm text-gray-500">
                    This job doesn't have any required or preferred skills defined.
                    Please update the job profile first.
                  </p>
                </div>
              )}

              {/* ── Phase 2: Years of Experience + Question Count per skill ── */}
              {showYearPanel && (
                <div className="mt-2 border-t border-gray-200 pt-5">
                  <h3 className="text-sm font-semibold text-gray-700 mb-4">
                    Configure Each Skill
                  </h3>
                  <div className="space-y-3">
                    {selectedSkillNames.map((skill) => {
                      const years = skillYears[skill] ?? 5
                      const questionCount = skillQuestionCounts[skill] ?? 10
                      return (
                        <div key={skill} className="bg-gray-50 rounded-lg px-4 py-3">
                          <div className="text-sm font-medium text-gray-800 mb-3">
                            {skill}
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {/* Years of Experience */}
                            <div>
                              <label className="text-xs text-gray-600 block mb-1">Years of Experience</label>
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => handleYearChange(skill, years - 1)}
                                  className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors text-base leading-none"
                                  aria-label={`Decrease years for ${skill}`}
                                >
                                  −
                                </button>
                                <input
                                  type="number"
                                  min={0}
                                  max={50}
                                  value={years}
                                  onChange={(e) => handleYearChange(skill, parseInt(e.target.value) || 0)}
                                  className="w-14 text-center border border-gray-300 rounded-md px-1 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  aria-label={`Years of experience for ${skill}`}
                                />
                                <button
                                  type="button"
                                  onClick={() => handleYearChange(skill, years + 1)}
                                  className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors text-base leading-none"
                                  aria-label={`Increase years for ${skill}`}
                                >
                                  +
                                </button>
                                <span className="text-xs text-gray-500 w-8">
                                  yr{years !== 1 ? 's' : ''}
                                </span>
                              </div>
                            </div>
                            
                            {/* Question Count */}
                            <div>
                              <label className="text-xs text-gray-600 block mb-1">Number of Questions</label>
                              <select
                                value={questionCount}
                                onChange={(e) => handleQuestionCountChange(skill, parseInt(e.target.value))}
                                className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                aria-label={`Question count for ${skill}`}
                              >
                                <option value={1}>1 question</option>
                                <option value={2}>2 questions</option>
                                <option value={3}>3 questions</option>
                                <option value={4}>4 questions</option>
                                <option value={5}>5 questions</option>
                                <option value={6}>6 questions</option>
                                <option value={7}>7 questions</option>
                                <option value={8}>8 questions</option>
                                <option value={9}>9 questions</option>
                                <option value={10}>10 questions</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={selectedSkillNames.length !== requiredCount}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Continue with {selectedSkillNames.length} Skill{selectedSkillNames.length !== 1 ? 's' : ''}
          </button>
        </div>
      </div>
    </div>
  )
}
